<template>
    <div>
        <div v-show="responseMessage" class="alert alert-danger">{{ responseMessage }}</div>
        <table class="table">
            <tbody>
                <tr>
                    <th style="border-top: 0">{{ officeLabel }}:</th>
                    <td style="border-top: 0" class="text-end">{{ officeName }}</td>
                </tr>
                <tr>
                    <th>{{ $t('Navn') }}:</th>
                    <td class="text-end">{{ studentName }}</td>
                </tr>
                <tr v-if="displaySaldo !== 'none'">
                    <th v-if="displaySaldo === 'total'">{{ balanceLabel }} totalt:</th>
                    <th v-if="displaySaldo !== 'total'">{{ balanceLabel }} per {{ displaySaldoDate }}:</th>
                    <td class="text-end">{{ balanceFormatted }}</td>
                </tr>
                <tr>
                    <th style="vertical-align: middle">{{ $t('Beløp å betale') }}:</th>
                    <td v-if="amountIsEditable">
                        <input class="form-control text-end" name="amount" type="number" min="0" :max="maxSum" :value="sum" @keypress="validateValue" @change="setSum" @keyup="setSum" />
                    </td>
                    <td class="text-end" v-if="!amountIsEditable">
                        {{ amountFormatted }}
                        <input type="hidden" name="amount" :value="amount">
                    </td>
                </tr>
                <tr v-if="studentPayCardFee">
                    <th colspan="2">{{ $t('Straksbetaling') }} <span>{{ transactionFee }}%</span></th>
                </tr>
                <tr v-if="studentPayCardFee">
                    <th>{{ $t('Totalsum til {officeName}', {officeName: officeName}) }}:</th>
                    <td class="text-end"><span id="finalSum">{{sumIncludingFee}}</span> kr</td>
                </tr>
    
                <tr v-if="paymentType === 'Vipps'">
                    <th style="vertical-align: middle">{{ $t('Betal fra mobil') }}:</th>
                    <td class="text-end">
                        <select class="form-select" name="mobile" @change="setMobile" v-if="mobileNumbers.length">
                            <option v-for="mobileNumber in mobileNumbers" :value="mobileNumber">{{ mobileNumber }}</option>
                        </select>
                        <span v-if="!mobileNumbers.length">{{ $t('Ingen gyldige mobilnummer') }}</span>
                    </td>
                </tr>
            </tbody>
        </table>
        <input type="hidden" name="fee" id="fee" :value="transactionFee" />

        <div class="row justify-content-center" v-show="!isLoading && !sent">
            <div class="col-sm-6 col-12 order-1 order-sm-0 mb-1">
                <a href="/" class="btn btn-secondary btn-block btn-sm-inline cursor-pointer">{{ $t('Gå til {brandName}', {brandName}) }}</a>
            </div>
            <div class="col-sm-6 col-12 order-0 order-sm-1 mb-1 text-end">
                <div v-if="paymentType === 'Vipps'">
                    <button @click="send()" class="btn btn-primary btn-block btn-sm-inline" :disabled="isLoading || !mobileNumbers.length">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-show="isLoading"></span>
                        Send Vipps
                    </button>
                </div>
                <div v-else>
                    Send generic
                </div>
            </div>
        </div>
        <div class="text-center col-12" v-show="isLoading">
            <span class="spinner-border" role="status"></span>
        </div>
        <p v-if="infoText" class="text-center" style="margin-top: 25px; color: #777">{{infoText}}</p>
        <PaymentResponse :paymentHash="paymentHash" :orderId="Store.getOrderId()"></PaymentResponse>
    </div>
</template>

<style scoped>
    select {text-align-last: right;}
    option {direction: rtl;}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Api from '../../api/BaseRequest';
import Store from '../../state/paymentStore';
import PaymentResponse from './PaymentResponse.vue';

const props = defineProps(['paymentData', 'paymentHash']);
const emit = defineEmits(['inprogress', 'error']);

const responseMessage = ref('');
const loading = ref(false);
const sent = ref(false);
const sum = ref(props.paymentData.amount > 0 ? props.paymentData.amount : 0);
const maxSum = ref(20000);
const mobile = ref(props.paymentData.mobileNumbers ? props.paymentData.mobileNumbers[0] : null);
const paymentType = ref(props.paymentData.paymentType);

const send = () => {
    responseMessage.value = '';
    loading.value = true;

    if (isNaN(sum.value)) {
        responseMessage.value = 'Beløpet er ugyldig.';
        loading.value = false;
        return;
    }
    Api.Post(
        '/p_request/' + props.paymentHash,
        {
            token: CsrfToken.value,
            sum: sum.value,
            mobile: mobile.value,
        }
    ).then(res => {
        loading.value = false;
        sent.value = true;
        if (res.data) {
            if (res.data.redirect) {
                window.location = res.data.message;
                return;
            }
            if (res.data.transaction) {
                Store.setOrderId(res.data.transaction)
            }
            if (res.data.message) {
                responseMessage.value = res.data.message;
            }
        }
        if (res.inprogress) {
            emit('inprogress', { showForm: true });
        }
        if (res.html) {
            emit('error', res.html);
        }
    }).catch(e => {
        console.error(e);
        emit('error', $t('Noe gikk galt.'));
    });
};

const setSum = (event) => {
    if (parseInt(event.target.value) > maxSum.value) {
        event.target.value = maxSum.value;
    }

    let amount = event.target.value;
    if (isNaN(parseInt(amount))) {
        sum.value = 0;
        return;
    }

    sum.value = Math.round(amount);
};

const setMobile = (event) => {
    mobile.value = event.target.value;
};

const validateValue = (event) => {
    if ([',', '.', '-'].indexOf(event.key) !== -1) {
        event.preventDefault();
    }
};

const brandName = computed(() => window.brandingName.toUpperCase());
const officeLabel = computed(() => $t('Trafikkskole'));
const officeName = computed(() => props.paymentData.office_name);
const studentName = computed(() => props.paymentData.student_full_name);
const displaySaldo = computed(() => props.paymentData.display_saldo);
const displaySaldoDate = computed(() => props.paymentData.display_saldo_date);
const balanceLabel = computed(() => props.paymentData.balance > 0 ? 'Skyldig' : 'Til gode');
const balanceFormatted = computed(() => props.paymentData.balance_formatted);
const amountFormatted = computed(() => props.paymentData.amount_formatted);
const amountIsEditable = computed(() => props.paymentData.amount_is_editable);
const amount = computed(() => props.paymentData.amount);
const sumIncludingFee = computed(() => {
    if (sum.value <= 0 || transactionFee.value <= 0) {
        return sum.value;
    }
    const fee = Math.max(1, Math.round(sum.value * transactionFee.value / 100));
    return sum.value + fee;
});
const studentPayCardFee = computed(() => props.paymentData.student_pay_card_fee === 1);
const transactionFee = computed(() => props.paymentData.card_fee);
const mobileNumbers = computed(() => props.paymentData.mobileNumbers);
const CsrfToken = computed(() => props.paymentData.csrf_token);
const infoText = computed(() => props.paymentData.infoText);
const isLoading = computed(() => loading.value);

onMounted(() => {
    if (props.paymentData && props.paymentData.auto_trigger_send && !loading.value && !sent.value && paymentType.value) {
        send();
    }
});
</script>
